import { useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ConfigType } from '../../../common/types';
import { useGetRequestTypes } from '../../../services/use-get-request-types';
import type { State as RequestTypesState } from '../../../services/use-get-request-types/types.tsx';
import { ANALYTIC_SCREEN_NAME, DEFAULT_CONFIG, HEX_COLOR_REGEX } from './constants';
import messages from './messages';
import type { UseWidgetSettingsForm, ValidateSpy, ValidatorFn, AnalyticAttributes } from './types';

export const hexColorValidator: ValidatorFn = (value, formatMessage) => {
	if (!value) {
		return formatMessage(messages.colorInputErrorMessage);
	}
	return HEX_COLOR_REGEX.test(value) ? undefined : formatMessage(messages.colorInputErrorMessage);
};

export const nonNilValidator: ValidatorFn = (value, formatMessage, fieldName = '') => {
	if (isNil(value)) {
		return formatMessage(messages.errorBlankValue, {
			fieldName: fieldName.toLocaleLowerCase(),
		});
	}
};

export const nonEmptyStringValidator: ValidatorFn = (value, formatMessage, fieldName = '') => {
	if (value?.length === 0) {
		return formatMessage(messages.errorBlankValue, {
			fieldName: fieldName.toLocaleLowerCase(),
		});
	}
};

export const maxLengthValidator =
	(fieldName: string, maxLength: number): ValidatorFn =>
	(value, formatMessage) => {
		if (value && value.length > maxLength) {
			return formatMessage(messages.errorMaxLength, {
				fieldName,
				length: maxLength,
			});
		}
	};

export const requiredWithMaxLengthValidator =
	(fieldName: string, maxLength: number): ValidatorFn =>
	(value, formatMessage) =>
		nonNilValidator(value, formatMessage, fieldName) ||
		nonEmptyStringValidator(value, formatMessage, fieldName) ||
		maxLengthValidator(fieldName, maxLength)(value, formatMessage);

export const getRequestOption = (requestTypes: RequestTypesState['data'], value: number | null) => {
	if (!requestTypes) {
		return undefined;
	}
	const matchingRequestType = requestTypes.find((requestType) => requestType.id === `${value}`);
	if (!matchingRequestType) {
		return undefined;
	}
	return {
		label: matchingRequestType.name,
		value: matchingRequestType.id,
	};
};

export const transformHexValue = (
	e: React.FormEvent<HTMLInputElement> | string,
	current: string,
): string => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { value } = typeof e === 'string' ? { value: e } : (e.target as HTMLInputElement);
	if (value === '') {
		// let user clear the input field
		return '#';
	}
	if (value[0] !== '#') {
		return current;
	}
	return value;
};

export const buildAnalyticAttributes = (
	previousConfiguration: ConfigType,
	currentConfiguration: ConfigType,
): AnalyticAttributes => ({
	enable: currentConfiguration.widgetEnabled,
	enablePrevious: previousConfiguration.widgetEnabled,
	defaultRequestType: currentConfiguration.requestTypeId,
	defaultRequestTypePrevious: previousConfiguration.requestTypeId,
	allowSearchForRequest: currentConfiguration.rtSearchEnabled,
	allowSearchForRequestPrevious: previousConfiguration.rtSearchEnabled,
	allowSearchForKnowledgeBase: currentConfiguration.kbEnabled,
	allowSearchForKnowledgeBasePrevious: previousConfiguration.kbEnabled,
	colorChange: currentConfiguration.primaryColorHex !== previousConfiguration.primaryColorHex,
	widgetBackgroundPattern: currentConfiguration.backgroundPattern,
	widgetBackgroundPatternPrevious: previousConfiguration.backgroundPattern,
	widgetButton: currentConfiguration.widgetButtonTextEnabled ? 'Custom text' : 'icon',
	widgetButtonPrevious: previousConfiguration.widgetButtonTextEnabled ? 'Custom text' : 'icon',
	widgetHeaderChanged: currentConfiguration.headerText !== previousConfiguration.headerText,
	confirmationMessageChanged:
		currentConfiguration.confirmationText !== previousConfiguration.confirmationText,
	widgetType: currentConfiguration.widgetType === 'VIRTUAL_AGENT' ? 'Virtual agent' : 'Basic',
	widgetTypePrevious:
		previousConfiguration.widgetType === 'VIRTUAL_AGENT' ? 'Virtual agent' : 'Basic',
});

export const useWidgetSettingsForm: UseWidgetSettingsForm = ({
	config,
	onChange,
	projectKey,
	formatMessage,
	onSubmit,
	isEnableDisabled,
}) => {
	const configValues = config || DEFAULT_CONFIG;
	const configState = { ...configValues, widgetEnabled: !configValues.widgetEnabled };
	const [currentConfig, setConfigState] = useState<ConfigType>(configState);
	const [lastConfig, setLastConfig] = useState<ConfigType>(configState);
	const [initialConfig, setInitialConfig] = useState<ConfigType>(configState);

	const [requestTypesState, fetchRequestTypes] = useGetRequestTypes(projectKey);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [cachedRequestTypes, setCachedRequestTypes] = useState<
		RequestTypesState['data'] | undefined
	>();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const validateSpy: ValidateSpy = (fieldName, validatorFn) => (value) => {
		let validationResult: string | undefined;

		if (isString(value) && validatorFn) {
			validationResult = validatorFn(value, formatMessage);
		}

		if (validationResult) {
			return validationResult;
		}

		let newValue = value;

		if (typeof value === 'object') {
			newValue = value?.value;
		}

		if (
			typeof value === 'object' &&
			fieldName === 'requestTypeId' &&
			value &&
			typeof value.value === 'string'
		) {
			newValue = Number(value?.value);
		}

		if (fieldName === 'widgetButtonTextEnabled') {
			newValue = value === 'text';
		}
		if (fieldName === 'requestTypeId' && !value) {
			// if previous value was present, then keep it
			if (currentConfig.requestTypeId) {
				newValue = currentConfig.requestTypeId;
			} else if (requestTypesState.data?.length) {
				newValue = requestTypesState.data[0].id;
			}
		}
		setConfigState((presentConfig) => ({ ...presentConfig, [fieldName]: newValue }));
	};

	// to fetch request types on search query change
	useEffect(() => {
		fetchRequestTypes(searchQuery);
	}, [fetchRequestTypes, searchQuery]);

	// to set default request type id
	useEffect(() => {
		if (requestTypesState.data && requestTypesState.data.length && !currentConfig.requestTypeId) {
			setConfigState((presentConfig) => ({
				...presentConfig,
				requestTypeId: Number(requestTypesState.data?.[0].id),
			}));
		}
	}, [config, currentConfig.requestTypeId, requestTypesState.data]);

	// call onchange only on actual config change
	useEffect(() => {
		if (onChange) {
			if (!isEqual(currentConfig, lastConfig)) {
				if (
					lastConfig.requestTypeId !== null &&
					lastConfig.requestTypeId !== currentConfig.requestTypeId
				) {
					fireUIAnalytics(
						createAnalyticsEvent({
							actionSubjectId: currentConfig.requestTypeId,
							source: ANALYTIC_SCREEN_NAME,
						}),
						'requestType changed',
						{
							oldRequestTypeId: lastConfig.requestTypeId,
							newRequestTypeId: currentConfig.requestTypeId,
						},
					);
				}
				onChange(currentConfig);
				setLastConfig(currentConfig);
			}
		}
	}, [createAnalyticsEvent, currentConfig, lastConfig, onChange]);

	const isFormDirty = useMemo(() => {
		if (!isEqual(currentConfig, initialConfig)) {
			// corner case to skip when  there is no existing config in server
			if (initialConfig.requestTypeId === null) {
				// check if anything else is changed
				const initialConfigCopy = {
					...initialConfig,
					// if requestTypeId is null, then it should be the first request type
					requestTypeId: currentConfig.requestTypeId,
					// if isEnableDisabled is true, then it will be overridden by the currentConfig
					widgetEnabled: isEnableDisabled
						? currentConfig.widgetEnabled
						: initialConfig.widgetEnabled,
				};
				if (isEqual(currentConfig, initialConfigCopy)) {
					return false;
				}
				return true;
			}
			return true;
		}
		return false;
	}, [currentConfig, initialConfig, isEnableDisabled]);

	// to prevent the form from rendering initially before request type loads
	// and also to prevent it from rerendering on every search query change
	useEffect(() => {
		if (requestTypesState.data) {
			setCachedRequestTypes(requestTypesState.data);
		}
	}, [requestTypesState.data]);
	const onFormSubmit = async () => {
		if (currentConfig) {
			const updatedCurrentConfig = {
				...currentConfig,
				widgetEnabled: !currentConfig.widgetEnabled,
			};
			onSubmit(updatedCurrentConfig).then((result) => {
				const [data, operation] = result;
				if (data) {
					setInitialConfig(currentConfig);
					fireTrackAnalytics(
						createAnalyticsEvent({
							source: ANALYTIC_SCREEN_NAME,
						}),
						`widgetSettings ${operation}`,
						buildAnalyticAttributes(initialConfig, currentConfig),
					);
				}
			});
		}
	};

	return [
		currentConfig,
		initialConfig,
		validateSpy,
		{ ...requestTypesState, data: cachedRequestTypes },
		setSearchQuery,
		onFormSubmit,
		isFormDirty,
	];
};
